// ============================================================
// calculations.js
// Shared statistical helpers for Dashboard and Findings
// ============================================================

/**
 * Returns stats for a single category.
 * @param {Object} category - from AUDIT_CATEGORIES
 * @param {Object} responses - keyed by criterion id
 */
export function calcCategoryStats(category, responses) {
  const criteria = category.criteria;
  let compliant = 0;
  let partial = 0;
  let nonCompliant = 0;
  let notApplicable = 0;
  let answered = 0;

  criteria.forEach((crit) => {
    const r = responses[crit.id];
    if (!r || !r.status) return;
    answered++;
    if (r.status === "Compliant") compliant++;
    else if (r.status === "Partially Compliant") partial++;
    else if (r.status === "Non-Compliant") nonCompliant++;
    else if (r.status === "Not Applicable") notApplicable++;
  });

  const scoreable = answered - notApplicable; // items that count toward score
  // Scoring: Compliant = 1, Partial = 0.5, Non-Compliant = 0, N/A = excluded
  const score = scoreable > 0
    ? Math.round(((compliant + partial * 0.5) / scoreable) * 100)
    : null;

  return {
    total: criteria.length,
    answered,
    compliant,
    partial,
    nonCompliant,
    notApplicable,
    scoreable,
    score, // null if no scoreable items answered
  };
}

/**
 * Returns overall audit stats across all selected categories.
 */
export function calcOverallStats(categories, responses) {
  let totalCompliant = 0;
  let totalPartial = 0;
  let totalNonCompliant = 0;
  let totalNA = 0;
  let totalAnswered = 0;
  let totalScorable = 0;

  categories.forEach((cat) => {
    const s = calcCategoryStats(cat, responses);
    totalCompliant += s.compliant;
    totalPartial += s.partial;
    totalNonCompliant += s.nonCompliant;
    totalNA += s.notApplicable;
    totalAnswered += s.answered;
    totalScorable += s.scoreable;
  });

  const overallScore = totalScorable > 0
    ? Math.round(((totalCompliant + totalPartial * 0.5) / totalScorable) * 100)
    : 0;

  return {
    totalCompliant,
    totalPartial,
    totalNonCompliant,
    totalNA,
    totalAnswered,
    totalScorable,
    overallScore,
  };
}

/**
 * Returns all action items (items with non-empty actionItem or non-compliant status).
 */
export function collectActionItems(categories, responses) {
  const items = [];
  categories.forEach((cat) => {
    cat.criteria.forEach((crit) => {
      const r = responses[crit.id];
      if (!r) return;
      if (
        r.actionItem?.trim() ||
        r.status === "Non-Compliant" ||
        r.status === "Partially Compliant"
      ) {
        items.push({
          categoryName: cat.name,
          citation: crit.citation,
          requirement: crit.requirement,
          ...r,
        });
      }
    });
  });
  return items;
}

/**
 * Returns immediate action items only.
 */
export function collectImmediateItems(categories, responses) {
  return collectActionItems(categories, responses).filter(
    (item) => item.priority === "Immediate"
  );
}

/**
 * Sort categories by score ascending (least compliant first).
 */
export function rankCategoriesByScore(categories, responses) {
  return categories
    .map((cat) => ({
      cat,
      stats: calcCategoryStats(cat, responses),
    }))
    .filter((x) => x.stats.score !== null)
    .sort((a, b) => a.stats.score - b.stats.score);
}

/** Score to rating label */
export function scoreToRating(score) {
  if (score === null) return "Not Rated";
  if (score >= 90) return "Good Standing";
  if (score >= 75) return "Satisfactory";
  if (score >= 50) return "Needs Improvement";
  return "Critical";
}

/** Score to CSS class */
export function scoreToClass(score) {
  if (score === null) return "score-null";
  if (score >= 90) return "score-good";
  if (score >= 75) return "score-satisfactory";
  if (score >= 50) return "score-needs";
  return "score-critical";
}
